import { useEffect, useState } from 'react';
import { getToken } from '../lib/api';
import { useT } from '../lib/i18n';
import { alert, haptic } from '../lib/telegram';
import { PinnedSheet, SetRow } from './SetSheet';

/**
 * Настройки → «Сводка»: the daily brief in Telegram (server: coach/brief.ts).
 * On or off; «Вечером накануне» (tomorrow's sessions, 21:00 by default) or
 * «Утром» (today's, 06:00, or 30 minutes before an earlier first session);
 * the time.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { ...init, headers: { ...(init.body ? { 'content-type': 'application/json' } : {}), authorization: `Bearer ${getToken()}` } });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error ?? `http_${res.status}`); }
  return res.json();
}

export interface BriefSettings { on: boolean; mode: 'evening' | 'morning'; time: string; explicit: boolean }
export const getBrief = () => api<BriefSettings>('/api/coach/brief');
export const putBrief = (b: Partial<BriefSettings>) => api<BriefSettings>('/api/coach/brief', { method: 'PUT', body: JSON.stringify(b) });

export function BriefRow() {
  const t = useT();
  const [s, setS] = useState<BriefSettings | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { getBrief().then(setS).catch(() => setS(null)); }, [open]);
  const status = !s ? '' : !s.on ? t('br.off') : t(s.mode === 'evening' ? 'br.statusEvening' : 'br.statusMorning', { time: s.time });
  return (
    <>
      <SetRow id="setup-brief" icon="🗒️" title={t('br.title')} status={status} onOpen={() => setOpen(true)} />
      {open && <BriefSheet onClose={() => setOpen(false)} />}
    </>
  );
}

function BriefSheet({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [s, setS] = useState<BriefSettings | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getBrief().then(setS).catch(() => setS(null)); }, []);
  async function save(patch: Partial<BriefSettings>) {
    setBusy(true);
    try { setS(await putBrief(patch)); haptic.tap(); }
    catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); }
    finally { setBusy(false); }
  }
  return (
    <PinnedSheet title={t('br.title')} onClose={onClose}
      footer={<button className="savebtn" onClick={onClose}>{t('co.set.done')}</button>}>
      {!s ? <div className="skeleton" /> : (<>
        <button className={`cat-card${s.on ? ' cat-card--on' : ''}`} role="switch" aria-checked={s.on} disabled={busy}
          onClick={() => save({ on: !s.on })}>
          <span className="cat-card__main"><b>{t('br.on')}</b><small>{s.on ? t(s.mode === 'evening' ? 'br.statusEvening' : 'br.statusMorning', { time: s.time }) : t('br.off')}</small></span>
          <span className={`cat-switch${s.on ? ' cat-switch--on' : ''}`} aria-hidden="true"><i /></span>
        </button>
        {s.on && (<>
          <div className="br-modes">
            <button className="chip" aria-pressed={s.mode === 'evening'} disabled={busy} onClick={() => save({ mode: 'evening' })}>{t('br.evening')}</button>
            <button className="chip" aria-pressed={s.mode === 'morning'} disabled={busy} onClick={() => save({ mode: 'morning' })}>{t('br.morning')}</button>
          </div>
          <label className="mf br-time"><span className="mf__label">{t('br.time')}</span>
            <input type="time" className="notearea" value={s.time} disabled={busy}
              onChange={(e) => { if (/^\d\d:\d\d$/.test(e.target.value)) save({ time: e.target.value }); }} /></label>
          <div className="card__when">{t(s.mode === 'evening' ? 'br.hintEvening' : 'br.hintMorning')}</div>
        </>)}
      </>)}
    </PinnedSheet>
  );
}
